/**
 * Auth flow against PocketBase. Email + password with forced-reset on first
 * login.
 *
 * Bootstrap pattern: every seeded user starts with the shared default password
 * `Welcome3873` (the building street number) and `must_change_password=true`.
 * AuthGuard redirects any user with must_change_password=true into
 * /change-password (only /login, /logout, and /change-password are reachable
 * until the flag flips false).
 *
 * Flow:
 *   1. signIn(email, password) -> authStore populated (persisted in localStorage)
 *   2. changePassword(current, next) -> rotates password, clears must_change_password,
 *      reauthenticates so the auth model in state reflects the flipped flag
 *   3. signOut() -> clears authStore
 */

import { useEffect, useState } from 'react';

import { currentUser, pb, type SunsetHarborUserRecord } from './pb';

export async function signIn(email: string, password: string): Promise<SunsetHarborUserRecord> {
  await pb.collection('users').authWithPassword(email.trim().toLowerCase(), password);
  const user = currentUser();
  if (!user) {
    throw new Error('Auth succeeded but no user record loaded. Inspect pb.authStore.');
  }
  return user;
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const user = currentUser();
  if (!user) {
    throw new Error('Not signed in.');
  }
  await pb.collection('users').update(user.id, {
    oldPassword: currentPassword,
    password: newPassword,
    passwordConfirm: newPassword,
    must_change_password: false,
  });
  // PB invalidates the auth token after a password change. Re-authenticate
  // immediately with the new password so the user doesn't have to sign in
  // again, and so authStore.model reflects must_change_password=false.
  await pb.collection('users').authWithPassword(user.email, newPassword);
}

export function signOut(): void {
  pb.authStore.clear();
}

export interface AuthState {
  user: SunsetHarborUserRecord | null;
  isAuthenticated: boolean;
  mustChangePassword: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

function snapshot(): AuthState {
  const user = currentUser();
  return {
    user,
    isAuthenticated: pb.authStore.isValid && user !== null,
    mustChangePassword: user?.must_change_password === true,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isSuperAdmin: user?.role === 'super_admin',
  };
}

/**
 * React hook returning the current auth state. Re-renders when the authStore
 * changes (signin, signout, password rotation, token refresh).
 */
export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>(() => snapshot());

  useEffect(() => {
    const unsubscribe = pb.authStore.onChange(() => {
      setState(snapshot());
    });
    return () => {
      unsubscribe();
    };
  }, []);

  return state;
}
